// Maps Solidity custom-error names to user-facing messages, across every Morpho
// contract. `COVERED_ABIS` in the spec is the authoritative list of what is
// covered; other interfaces are added here as they are touched.
//
// Name collisions across protocols (e.g. `Unauthorized`) are deliberately deduped
// to one canonical message: the underlying meaning is the same.
//
// Errors with args (e.g. metamorpho's `UnauthorizedMarket(Id id)`) aren't
// supported yet; when we need to interpolate, the lookup can return a function
// `(args) => string` instead of a static string. viem already populates
// `revertError.data?.args` for us.
//
// Keep this in sync with the protocol interfaces in `@repo/contracts`
// (solidity/interfaces/*.sol). `morpho-errors.utils.spec.ts` asserts that every
// error declared by the covered ABIs has a message here, and vice versa.

/** @internal */
export const MORPHO_ERROR_MESSAGES: Record<string, string> = {
  // --- Midnight (IMidnight.sol) ---
  AlreadyConsumed: 'This offer has already been used',
  BuyerGatedFromIncreasingCredit: 'You are not allowed to lend in this market',
  CannotIncreaseDebtPostMaturity: 'You cannot borrow more after this market has matured',
  CollateralParamsNotSorted: 'Collateral parameters must be sorted',
  ConsumedAssets: 'This offer has been fully consumed',
  ConsumedUnits: 'This offer has been fully consumed',
  ContinuousFeeAboveMax: 'Continuous fee is too high',
  ContinuousFeeAboveOfferCap: 'Continuous fee exceeds the offer cap',
  FeeNotMultipleOfFeeCbp: 'Fee must be a multiple of the fee CBP',
  InconsistentInput: 'Input parameters are inconsistent',
  InvalidChainId: 'This offer is not valid on this network',
  InvalidFeeIndex: 'Invalid fee index',
  InvalidLiquidationCursor: 'Invalid liquidation cursor',
  InvalidLltv: 'This LLTV is invalid',
  InvalidMaxLif: 'Invalid max liquidation incentive factor',
  InvalidMidnight: 'Invalid Midnight contract address',
  InvalidOfferCaps: 'Invalid offer caps',
  InvalidTickSpacing: 'Invalid tick spacing',
  LiquidationCursorNotEnabled: 'Liquidation cursor is not enabled for this market',
  LiquidatorGatedFromLiquidating: 'You are not allowed to liquidate in this market',
  LltvNotEnabled: 'This LLTV is not enabled',
  MakerCreditOrDebtIncreased: 'Order state has changed; please refresh and try again',
  MarketLossFactorMaxedOut: 'This market has reached its maximum loss factor',
  MarketNotCreated: "This market hasn't been created yet",
  MaturityTooFar: "This market's maturity is too far in the future",
  MaxLifTooHigh: 'Max liquidation incentive factor is too high',
  NoCollateralParams: 'Market has no collateral parameters',
  NotBorrower: 'Only the borrower can perform this action',
  NotLiquidatable: 'This position is not liquidatable',
  OfferExpired: 'This offer has expired',
  OfferNotStarted: "This offer hasn't started yet",
  OnlyConfigurator: 'Only the configurator can call this function',
  OnlyFeeClaimer: 'Only the fee claimer can call this function',
  OnlyFeeSetter: 'Only the fee setter can call this function',
  OnlyTickSpacingSetter: 'Only the tick-spacing setter can call this function',
  RatifierFailed: 'Offer signature or proof is invalid',
  RatifierUnauthorized: 'You are not authorized to take this offer',
  RecoveryCloseFactorConditionsViolated: 'Recovery close-factor conditions are not met',
  SelfTake: 'You cannot take your own offer',
  SellerGatedFromIncreasingDebt: 'You are not allowed to borrow in this market',
  SellerIsLiquidatable: 'Your position is liquidatable and cannot increase debt',
  SettlementFeeAboveMax: 'Settlement fee is too high',
  TakerUnauthorized: 'You are not authorized to take this offer',
  TickNotAccessible: 'Selected rate is not available for this market',
  TooManyActivatedCollaterals: 'Too many active collaterals on this position',
  TooManyCollateralParams: 'Too many collateral parameters',
  Unauthorized: 'You are not authorized to perform this action',
  UnhealthyBorrower: 'This trade would put your position at liquidation risk',
  UnusedReceiverMustBeZero: 'Receiver must be the zero address when unused',
  WrongBuyCallbackReturnValue: 'Buy callback returned an invalid value',
  WrongFlashLoanCallbackReturnValue: 'Flash loan callback returned an invalid value',
  WrongLiquidateCallbackReturnValue: 'Liquidate callback returned an invalid value',
  WrongRepayCallbackReturnValue: 'Repay callback returned an invalid value',
  WrongSellCallbackReturnValue: 'Sell callback returned an invalid value',

  // --- Midnight ratifiers (IEcrecoverRatifier.sol, ISetterRatifier.sol) ---
  InvalidProof: 'Offer proof is invalid',
  // Also thrown by IBlueBuyCallback.setAuthorizationWithSig, so this must not name the offer.
  InvalidSignature: 'This signature is invalid',
  NotRatified: 'This offer is not currently ratified',
  RootCanceled: 'This offer has been cancelled by the maker',

  // --- Midnight bundles (IMidnightBundles.sol) ---
  DeadlinePassed: 'The deadline for this transaction has passed; please refresh and try again',
  InconsistentMarket: 'Order data is out of date; please refresh and try again',
  InconsistentSide: 'Order data is out of date; please refresh and try again',
  NotReduceOnly: 'This trade can only reduce your existing position',
  OutOfOffers: 'Not enough offers available to fill this trade',
  PctExceeded: 'Referral fee is too high',
  SellerAssetsTooLow: 'Trade would receive less than the minimum amount',
  UnitsTooHigh: 'Trade exceeds the maximum unit amount',
  UnitsTooLow: 'Trade falls below the minimum unit amount',

  // --- Midnight bundles V2 (IMidnightBundlesV2.sol) ---
  // DeadlinePassed and ContinuousFeeAboveMax share their existing entries.
  ConsumedAboveMax:
    'Your order was filled before it could be updated; please refresh and try again',
  InconsistentBlue: 'Inconsistent Morpho Blue contract address',
  InconsistentMidnight: 'Inconsistent Midnight contract address',
  InvalidRatifierResponse: 'Your order could not be confirmed; please try again',

  // --- Blue buy callback (IBlueBuyCallback.sol) ---
  // `InvalidSignature` is also declared here; it shares the ratifier entry above.
  AuthorizationExpired: 'This authorization has expired; please refresh and try again',
  InconsistentLoanToken: 'This offer and the market holding its funds use different loan tokens',
  InvalidNonce: 'This authorization has already been used; please refresh and try again',
  NotMidnight: 'Only Midnight can settle against these funds',
  NotOwner: 'You are not the owner of these escrowed funds',
  NotOwnerBuyer: 'Only the owner of these escrowed funds can buy with them',

  // --- Public allocator (IBluePublicAllocator.sol) ---
  CannotPullFromIdle: 'The public allocator is not allowed to move this vault’s idle liquidity',
  CannotPullFromMarket: 'The public allocator is not allowed to move liquidity out of this market',
  InactiveAdapter: 'The public allocator is not enabled for this adapter',
  IncorrectPenalty: 'The public allocator fee has changed; please refresh and try again',
  NotVaultV2: 'This address is not a Morpho V2 vault',

  // --- Shared by IVault.sol and IBluePublicAllocator.sol; keep contract-neutral ---
  AbsoluteCapExceeded: 'This allocation would exceed the absolute cap',
  PenaltyTooHigh: 'This penalty exceeds the maximum allowed'
}

export function getMorphoErrorMessage(errorName: string | undefined): string | undefined {
  if (!errorName) return undefined
  return MORPHO_ERROR_MESSAGES[errorName]
}
